import useUpdateEffect from '@restart/hooks/useUpdateEffect';
import classNames from 'classnames/bind';
import React, { useState, useMemo, useRef } from 'react';
import { RiCheckboxBlankCircleLine, RiCheckboxCircleLine, RiMap2Line } from 'react-icons/ri';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';

import AccordionCaret from './AccordionCaret';
import styles from './MapSelector.module.scss';

import actions from '@/actions';
import { OL_DEFAULT_MAP_ID } from '@/config';
import { MAPS } from '@/define/olMaps';
import OlMap from '@/helpers/OlMap';
import { NotifierService as notifier } from '@/libs/Notifier';
import { getCSSVariable } from '@/utils/theme';

const cx = classNames.bind(styles);

const MapSelector = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const [showPanel, setShowPanel] = useState(false);
  const [currentItem, setCurrentItem] = useState({
    mapId: OL_DEFAULT_MAP_ID,
    typeIndex: 0,
  });
  const menuRef = useRef();
  const panelRef = useRef();

  const title = useMemo(() => {
    const { name, types } = MAPS[currentItem.mapId];
    return `${name} - ${types[currentItem.typeIndex].name}`;
  }, [currentItem]);

  // Allow 3D map only in Mission Hub
  const isAvailable3dMap = useMemo(() => {
    return location.pathname.startsWith('/mission-hub');
  }, [location]);

  useUpdateEffect(() => {
    if (showPanel) {
      document.addEventListener('mousedown', unfocusPanel);
    }

    return () => {
      document.removeEventListener('mousedown', unfocusPanel);
    };
  }, [showPanel]);

  const unfocusPanel = (e) => {
    if (menuRef.current.contains(e.target)) return;
    if (panelRef.current.contains(e.target)) return;

    setShowPanel(false);
  };

  const togglePanel = () => {
    setShowPanel(!showPanel);
  };

  const changeMap = (mapId, typeIndex) => {
    const { lat, lng } = OlMap.getCenter();
    if (mapId === 'VWORLD' && !OlMap.checkInsideOfVWorld([lng, lat])) {
      notifier.error('This location is not supported by VWorld.');
      return;
    }

    togglePanel();
    setCurrentItem({ mapId, typeIndex });

    if (mapId === 'VWORLD' && MAPS['VWORLD'].types[typeIndex].key === '3d') {
      dispatch(actions.map3d.show());
    } else {
      dispatch(actions.map3d.hide());
      OlMap.changeMap(mapId, typeIndex);
    }
  };

  return (
    <div className={cx('container')}>
      <div ref={menuRef} className={cx('menu')} onClick={togglePanel}>
        <RiMap2Line size={20} color="white" />
        <div className={cx('name')}>{title}</div>
        <AccordionCaret up={showPanel} size={32} />
      </div>
      <div ref={panelRef} className={cx(['panel', { show: showPanel }])}>
        {Object.entries(MAPS).map(([mapId, map]) => (
          <div key={mapId} className={cx('group')}>
            <div className={cx('header')}>
              <div className={cx('name')}>{map.name}</div>
              {map.label && <div className={cx('label')}>{map.label}</div>}
            </div>
            <div className={cx('items')}>
              {map.types
                .filter((type) => type.key !== '3d' || isAvailable3dMap)
                .map((type, index) => {
                  const isActive = mapId === currentItem.mapId && index === currentItem.typeIndex;

                  return (
                    <div key={index} onClick={() => changeMap(mapId, index)} className={cx('item')}>
                      {isActive ? (
                        <RiCheckboxCircleLine size={14} color={getCSSVariable('--tint-color')} />
                      ) : (
                        <RiCheckboxBlankCircleLine size={14} color="white" />
                      )}
                      <div className={cx(['menuText', { active: isActive }])}>{type.name}</div>
                    </div>
                  );
                })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MapSelector;
